// Modal de félicitations levé quand un ou plusieurs comptes challenge synchronisés
// ont franchi leur target (fundedPromptState = "pending" côté serveur).
//
// PUREMENT INFORMATIF : on félicite, on ne demande rien. Les frais d'activation
// ne se saisissent PAS ici (un montant posé sur le compte challenge, sans
// fundedAt, n'apparaissait nulle part dans le Prop Firm Manager). Ils se
// renseignent sur le compte funded : rattaché automatiquement par la sync, ou
// via « Passer en Funded » pour les comptes non synchronisés.
//
// Fermer le modal ENREGISTRE la réponse ("confirmed", sans frais) : sinon il
// réapparaîtrait à chaque rafraîchissement. "confirmed" et non "declined" car
// c'est l'état qu'exige le rattachement funded automatique (matchFundedCandidate).

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getFundedPrompts,
  respondToFundedPrompts,
  useAction,
  useQuery,
} from "wasp/client/operations";
import { Info } from "lucide-react";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { useToast } from "../hooks/use-toast";
import useTranslation from "../hooks/useTranslation";
import { usePayoutCelebration } from "./usePayoutCelebration";

/**
 * Intervalle de poll : 5 minutes. Calé sur le cron de sync qui tourne toutes
 * les 15 minutes — un tiers de la période suffit à voir la sollicitation
 * apparaître rapidement sans marteler le serveur. La query est vide dans
 * l'immense majorité des appels.
 */
const FUNDED_PROMPT_POLL_INTERVAL_MS = 5 * 60 * 1000;

interface FundedPrompt {
  id: string;
  name: string;
  initialBalance: number;
  currency: string;
  targetReachedAt: string | Date | null;
  propFirmName: string | null;
  propFirmLogoUrl: string | null;
  isSynced: boolean;
}

export default function FundedCelebrationModal() {
  const t = useTranslation();
  const { toast } = useToast();
  const { celebrate } = usePayoutCelebration();

  // Le poll est suspendu quand l'onglet est masqué : sans ça un onglet oublié
  // interroge le serveur pendant des heures pour rien.
  const [tabVisible, setTabVisible] = useState(
    () => typeof document === "undefined" || document.visibilityState !== "hidden",
  );

  useEffect(() => {
    if (typeof document === "undefined") return;
    const onVisibilityChange = () => {
      setTabVisible(document.visibilityState !== "hidden");
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, []);

  const { data, refetch } = useQuery(
    getFundedPrompts,
    {},
    { refetchInterval: tabVisible ? FUNDED_PROMPT_POLL_INTERVAL_MS : false },
  );

  const respond = useAction(respondToFundedPrompts);

  const prompts = (data as FundedPrompt[] | undefined) ?? [];

  // Clé stable dérivée des ids TRIÉS : la query refetch toutes les 5 minutes et
  // renvoie presque toujours la même liste (nouvel objet, même contenu). Sans
  // cette clé, les confettis repartiraient à chaque refetch.
  const promptsKey = useMemo(
    () =>
      prompts
        .map((p) => p.id)
        .sort()
        .join(","),
    [prompts],
  );

  const [dismissed, setDismissed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Rejoue les confettis uniquement quand l'ENSEMBLE des comptes change
  // (promptsKey), pas à chaque refetch.
  const lastCelebratedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!promptsKey) {
      lastCelebratedKeyRef.current = null;
      return;
    }
    if (lastCelebratedKeyRef.current === promptsKey) return;
    lastCelebratedKeyRef.current = promptsKey;

    setDismissed(false);
    celebrate();
  }, [promptsKey, celebrate]);

  const open = prompts.length > 0 && !dismissed;
  const anySynced = prompts.some((p) => p.isSynced);

  /**
   * Fermeture = accusé de lecture persisté. Une réponse par compte (ids
   * uniques par construction côté serveur, qui rejette les doublons), sans
   * frais. Le modal se ferme tout de suite ; en cas d'échec réseau l'état
   * reste "pending" et le modal reviendra au prochain chargement, ce qui est
   * le bon repli.
   */
  const close = useCallback(async () => {
    if (submitting) return;
    setDismissed(true);
    if (prompts.length === 0) return;

    setSubmitting(true);
    try {
      await respond({
        responses: prompts.map((p) => ({
          accountId: p.id,
          confirmed: true,
          activationFee: null,
        })),
      });
      refetch();
    } catch (err: any) {
      toast({
        title: t.tradingAccounts.error,
        description: err?.message,
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  }, [submitting, prompts, respond, refetch, toast, t]);

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) void close();
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t.tradingAccounts.fundedCelebrationTitle}</DialogTitle>
          <DialogDescription>
            {t.tradingAccounts.fundedCelebrationSubtitle}
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[50vh] space-y-2 overflow-y-auto py-2">
          {prompts.map((p) => (
            <div
              key={p.id}
              className="flex items-center gap-3 rounded-lg border border-border bg-card p-3"
            >
              {p.propFirmLogoUrl && (
                <img
                  src={p.propFirmLogoUrl}
                  alt=""
                  className="h-8 w-8 shrink-0 rounded object-contain"
                />
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{p.name}</p>
                {p.propFirmName && (
                  <p className="truncate text-xs text-muted-foreground">
                    {p.propFirmName}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>

        {anySynced && (
          <div className="flex gap-2 rounded-lg border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
            <Info className="mt-0.5 h-4 w-4 shrink-0" />
            <p>{t.tradingAccounts.fundedCelebrationSyncNote}</p>
          </div>
        )}

        <DialogFooter>
          <Button onClick={() => void close()} disabled={submitting}>
            {t.tradingAccounts.fundedCelebrationCloseButton}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
